import {first} from 'lodash';
import {connect} from 'react-redux';

import {getTimeFormat} from '../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {CellsPanelId, ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {makeMapStateToProps, wrapInShallowSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {createConversationBaseLightStabilizer} from '../../../helpers/models/conversationStabilityHelpers';
import {AppState} from '../../../store/appState';
import {makeGetVisualRecipient} from '../../../store/data/contacts/recipientsSelectors';
import {
  makeGetAreAllArchived,
  makeGetAreAllInternal,
  makeGetAreAllSpam,
  makeGetAreAllTrashed,
  makeGetReminderForAll,
  makeGetRemindersForAll,
} from '../../../store/data/conversations/conversationsMultiSelectors';
import {
  ConversationBasesSelector,
  makeGetConversationContextNamespaces,
  makeGetConversationsInboxes,
  OptionalConversationBaseSelector,
} from '../../../store/data/conversations/conversationsSelectors';
import {InboxesVisualConversationSelection} from '../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {
  makeGetKnownSelectedConversations,
  makeGetOnlySelectedConversation,
} from '../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {makeGetIsBulkActionRequired} from '../../../store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {InboxesTopbarActions} from './inboxesTopbarActions';

/*
 * Props.
 */

interface InboxesTopbarActionsContainerProps {
  panelId: ViewerPanelId;
  cellsPanelId: CellsPanelId | undefined;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: InboxesTopbarActionsContainerProps) {
  return props.cellsPanelId;
}

function getCellsContext(_state: unknown, props: InboxesTopbarActionsContainerProps) {
  return props.cellsContext;
}

function getInboxesParameters(_state: unknown, props: InboxesTopbarActionsContainerProps) {
  return props.inboxesParameters;
}

function getConversationSelection(_state: unknown, props: InboxesTopbarActionsContainerProps) {
  return props.conversationSelection;
}

function makeGetFirstConversation(
  getCurrentConversations: ConversationBasesSelector<InboxesTopbarActionsContainerProps>,
): OptionalConversationBaseSelector<InboxesTopbarActionsContainerProps> {
  const stabilizeConversation = createConversationBaseLightStabilizer();

  return (state, props) => stabilizeConversation(first(getCurrentConversations(state, props)));
}

function getTeammateId(_state: AppState, props: InboxesTopbarActionsContainerProps) {
  return props.cellsContext.teammateId;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InboxesTopbarActionsContainerProps>()(() => {
  const getCurrentConversations = makeGetKnownSelectedConversations(
    getConversationSelection,
    getCellsPanelId,
  );
  const getAreAllArchived = makeGetAreAllArchived(getCellsContext, getCurrentConversations);
  const getCurrentConversation = makeGetFirstConversation(getCurrentConversations);
  const getOnlyConversation = makeGetOnlySelectedConversation(getConversationSelection);

  return {
    onlyConversation: getOnlyConversation,
    conversations: getCurrentConversations,
    conversationSelection: getConversationSelection,
    isBulkActionRequired: makeGetIsBulkActionRequired(getInboxesParameters, getConversationSelection),
    recipient: makeGetVisualRecipient(
      getCurrentConversation,
      getTeammateId,
      'conversationsMoreCoordinatorContainer',
    ),
    areAllInternal: makeGetAreAllInternal(getCurrentConversations),
    areAllArchived: getAreAllArchived,
    areAllTrashed: makeGetAreAllTrashed(getCellsContext, getCurrentConversations),
    areAllSpam: makeGetAreAllSpam(getCellsContext, getCurrentConversations),
    conversationsInboxes: makeGetConversationsInboxes(getCurrentConversations),
    reminderForAll: makeGetReminderForAll(getCellsContext, getCurrentConversations),
    remindersForAll: makeGetRemindersForAll(getCellsContext, getCurrentConversations),
    contextNamespaces: wrapInShallowSelector(
      makeGetConversationContextNamespaces(getCellsContext, getCurrentConversations),
    ),
    timeFormat: getTimeFormat,
  };
});

export const InboxesTopbarActionsContainer = connect(
  mapStateToProps,
  null,
  (stateProps, _dispatchProps, ownProps) => {
    const {conversationSelection, ...remainingOwnProps} = ownProps;

    return {
      ...stateProps,
      ...remainingOwnProps,
    };
  },
)(InboxesTopbarActions);
